import type {
  FieldAdminView,
  FieldAudience,
  FieldLevel,
  FieldScopeChoices,
  FieldType,
} from "@fieldpath/shared";
import { FIELD_AUDIENCE_LABELS } from "@fieldpath/shared";
import { useCallback, useEffect, useState } from "react";

import { api, ApiError, type Fetcher } from "../api/client";
import { HelpButton } from "./HelpButton";

/**
 * FIELD DEFINITIONS on /admin (E3/4 row 5). After the first load the
 * database is the writer of these. A field's key, level and type are shown
 * and never offered for change: values are stored by type against subjects
 * of that level. Switching a field off hides it everywhere and keeps its
 * values. The rules are `db/field-admin.ts`'s; this offers the controls.
 */
const AUDIENCES = Object.keys(FIELD_AUDIENCE_LABELS) as FieldAudience[];
const TYPE_LABEL: Record<FieldType, string> = {
  dropdown: "dropdown",
  date: "date",
  text: "text",
  long_text: "long text",
  checkbox: "checkbox",
  number: "number",
};

export function FieldAdminPanel({ fetcher }: { fetcher: Fetcher }) {
  const [fields, setFields] = useState<readonly FieldAdminView[] | null>(null);
  const [choices, setChoices] = useState<FieldScopeChoices | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // The field just saved, marked "Saved." on its row for a few seconds
  // (Jeremy, 20 Oct 2026: renaming a field and pressing Save "did nothing" -
  // it saved, but the editor stayed open with no sign it had worked).
  const [justSaved, setJustSaved] = useState<string | null>(null);
  useEffect(() => {
    if (!justSaved) return;
    const t = setTimeout(() => setJustSaved(null), 4000);
    return () => clearTimeout(t);
  }, [justSaved]);

  const load = useCallback(() => {
    void api
      .fieldAdmin(fetcher)
      .then((r) => {
        setFields(r.fields);
        setChoices(r.choices);
        setError(null);
      })
      .catch((e: Error) => setError(e.message));
  }, [fetcher]);
  useEffect(load, [load]);

  if (error) {
    return (
      <div className="card admin-panel">
        <h2>Custom fields<HelpButton screens="admin-fields" /></h2>
        <p className="admin-warn">The field definitions could not be read: {error}</p>
      </div>
    );
  }
  if (!fields || !choices) return null;

  const off = fields.filter((f) => !f.isActive).length;
  const restricted = fields.filter((f) => f.isActive && f.visibleTo !== "everyone").length;

  return (
    <div className="card admin-panel">
      <h2>Custom fields<HelpButton screens="admin-fields" /></h2>
      <p className="muted">
        {fields.length} field{fields.length === 1 ? "" : "s"}
        {restricted > 0 ? `, ${restricted} restricted` : ""}
        {off > 0 ? `, ${off} switched off` : ""}. Values collected on projects, deliverables, tasks
        and stages.
      </p>
      <button className="linkish expander" onClick={() => setOpen((v) => !v)}>
        {open ? "Hide the fields" : "Show the fields"}
      </button>
      {open ? (
        <>
          {fields.map((f) =>
            editing === f.id ? (
              <FieldEditor
                key={f.id}
                field={f}
                choices={choices}
                fetcher={fetcher}
                onChanged={load}
                onClose={() => setEditing(null)}
                onSaved={() => {
                  setEditing(null);
                  setJustSaved(f.id);
                }}
              />
            ) : (
              <div key={f.id} className={`template-row ${f.isActive ? "" : "is-off"}`}>
                <div className="template-what">
                  <span className="template-name">{f.label}</span>
                  <span className="template-key">{f.key}</span>
                  {f.isActive ? null : <span className="template-off">OFF</span>}
                  {justSaved === f.id ? (
                    <span className="saved-note" role="status" style={{ margin: "0 0 0 8px", padding: "1px 8px" }}>
                      Saved.
                    </span>
                  ) : null}
                  <p className="muted">
                    {f.level} · {TYPE_LABEL[f.type]}
                    {f.multiSelect ? " (several)" : ""}
                    {f.required ? " · required" : ""}
                    {f.inGrid ? " · in the grid" : ""} · seen by {FIELD_AUDIENCE_LABELS[f.visibleTo]}, changed
                    by {FIELD_AUDIENCE_LABELS[f.editableBy]}
                    {f.scopes.length > 0 ? ` · only on ${f.scopes.map((s) => s.label).join(", ")}` : ""}
                    {` · ${f.valuesHeld} value${f.valuesHeld === 1 ? "" : "s"}`}
                  </p>
                </div>
                <div className="template-control">
                  <button className="secondary" onClick={() => setEditing(f.id)}>
                    Edit
                  </button>
                </div>
              </div>
            ),
          )}
          {creating ? (
            <NewField
              fetcher={fetcher}
              onCreated={(id) => {
                setCreating(false);
                load();
                setEditing(id);
              }}
              onCancel={() => setCreating(false)}
            />
          ) : (
            <div className="row" style={{ marginTop: 12 }}>
              <button className="secondary" onClick={() => setCreating(true)}>
                New field
              </button>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

function AudienceSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: FieldAudience;
  onChange: (a: FieldAudience) => void;
}) {
  return (
    <div className="field">
      <label>{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value as FieldAudience)}>
        {AUDIENCES.map((a) => (
          <option key={a} value={a}>
            {FIELD_AUDIENCE_LABELS[a]}
          </option>
        ))}
      </select>
    </div>
  );
}

function NewField({
  fetcher,
  onCreated,
  onCancel,
}: {
  fetcher: Fetcher;
  onCreated: (id: string) => void;
  onCancel: () => void;
}) {
  const [key, setKey] = useState("");
  const [label, setLabel] = useState("");
  const [level, setLevel] = useState<FieldLevel>("project");
  const [type, setType] = useState<FieldType>("text");
  const [multiSelect, setMultiSelect] = useState(false);
  const [visibleTo, setVisibleTo] = useState<FieldAudience>("everyone");
  const [editableBy, setEditableBy] = useState<FieldAudience>("everyone");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const create = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const { field } = await api.createField(fetcher, {
        key,
        label,
        level,
        type,
        multiSelect: type === "dropdown" && multiSelect,
        visibleTo,
        editableBy,
      });
      onCreated(field.id);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="field-editor">
      <h3 className="admin-subhead">New field</h3>
      <p className="muted">The key, level and type cannot be changed once the field exists.</p>
      <div className="field-editor-grid">
        <div className="field">
          <label>Key</label>
          <input value={key} onChange={(e) => setKey(e.target.value.toUpperCase())} placeholder="PERMIT_NO" />
        </div>
        <div className="field">
          <label>Label</label>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Permit number" />
        </div>
        <div className="field">
          <label>Level</label>
          <select value={level} onChange={(e) => setLevel(e.target.value as FieldLevel)}>
            <option value="project">project</option>
            <option value="deliverable">deliverable</option>
            <option value="task">task</option>
            <option value="stage">stage</option>
          </select>
        </div>
        <div className="field">
          <label>Type</label>
          <select value={type} onChange={(e) => setType(e.target.value as FieldType)}>
            {(Object.keys(TYPE_LABEL) as FieldType[]).map((t) => (
              <option key={t} value={t}>
                {TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </div>
        <AudienceSelect label="Seen by" value={visibleTo} onChange={setVisibleTo} />
        <AudienceSelect label="Changed by" value={editableBy} onChange={setEditableBy} />
      </div>
      {type === "dropdown" ? (
        <label className="field-flag">
          <input type="checkbox" checked={multiSelect} onChange={(e) => setMultiSelect(e.target.checked)} />
          Several choices may be picked
        </label>
      ) : null}
      {error ? <p className="error-inline">{error}</p> : null}
      <div className="row" style={{ justifyContent: "flex-end", gap: 8 }}>
        <button onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button className="primary" onClick={() => void create()} disabled={busy || key === "" || label.trim() === ""}>
          Create
        </button>
      </div>
    </div>
  );
}

function FieldEditor({
  field,
  choices,
  fetcher,
  onChanged,
  onClose,
  onSaved,
}: {
  field: FieldAdminView;
  choices: FieldScopeChoices;
  fetcher: Fetcher;
  onChanged: () => void;
  onClose: () => void;
  /** After Save succeeds: close the editor and mark the row "Saved.". */
  onSaved: () => void;
}) {
  const [label, setLabel] = useState(field.label);
  const [helpText, setHelpText] = useState(field.helpText ?? "");
  const [required, setRequired] = useState(field.required);
  const [visibleTo, setVisibleTo] = useState(field.visibleTo);
  const [editableBy, setEditableBy] = useState(field.editableBy);
  const [inGrid, setInGrid] = useState(field.inGrid);
  const [newOption, setNewOption] = useState("");
  const [scopeValue, setScopeValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<unknown>): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
      return true;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  // What this field's level can be limited to.
  // A limit already in place is not offered again (punch list #22); the
  // server refuses a duplicate either way.
  const scopeOptions: { value: string; label: string }[] = (
    field.level === "project"
      ? choices.projectTypes.map((t) => ({ value: `projectType:${t}`, label: t }))
      : field.level === "deliverable"
        ? choices.deliverableTypes.map((t) => ({ value: `deliverableTypeId:${t.id}`, label: t.name }))
        : field.level === "task"
          ? choices.workflows.map((w) => ({ value: `workflowId:${w.id}`, label: w.name }))
          : choices.workflows.flatMap((w) =>
              w.stages.map((s) => ({ value: `workflowStageId:${s.id}`, label: `${w.name} › ${s.name}` })),
            )
  ).filter((o) => !field.scopes.some((s) => s.label === o.label));

  return (
    <div className="field-editor">
      <h3 className="admin-subhead">
        {field.label} <span className="template-key">{field.key}</span>
      </h3>
      <p className="muted">
        A {field.level} field, {TYPE_LABEL[field.type]}
        {field.multiSelect ? " (several choices)" : ""} — fixed once created. {field.valuesHeld} value
        {field.valuesHeld === 1 ? "" : "s"} held.
      </p>
      <div className="field-editor-grid">
        <div className="field">
          <label>Label</label>
          <input value={label} onChange={(e) => setLabel(e.target.value)} />
        </div>
        <div className="field">
          <label>Help text</label>
          <input value={helpText} onChange={(e) => setHelpText(e.target.value)} />
        </div>
        <AudienceSelect label="Seen by" value={visibleTo} onChange={setVisibleTo} />
        <AudienceSelect label="Changed by" value={editableBy} onChange={setEditableBy} />
      </div>
      <div className="row" style={{ gap: 18 }}>
        <label className="field-flag">
          <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />
          Required
        </label>
        <label className="field-flag">
          <input type="checkbox" checked={inGrid} onChange={(e) => setInGrid(e.target.checked)} />
          In the grid
        </label>
      </div>

      {field.type === "dropdown" ? (
        <div className="field-editor-section">
          <div className="section-label">
            <span>Choices</span>
          </div>
          {field.options.map((o) => (
            <div key={o.id} className="field-editor-line">
              <span className={o.isActive ? undefined : "muted"}>
                {o.label}
                {o.isActive ? "" : " (off)"}
              </span>
              <button
                className="linkish"
                disabled={busy}
                onClick={() => void run(() => api.updateFieldOption(fetcher, o.id, { isActive: !o.isActive }))}
              >
                {o.isActive ? "Switch off" : "Switch on"}
              </button>
            </div>
          ))}
          <div className="row" style={{ gap: 8, marginTop: 6 }}>
            <input
              value={newOption}
              onChange={(e) => setNewOption(e.target.value)}
              placeholder="A new choice"
              style={{ maxWidth: 280 }}
            />
            <button
              className="secondary"
              disabled={busy || newOption.trim() === ""}
              onClick={() =>
                void run(() => api.addFieldOption(fetcher, field.id, newOption)).then((ok) => {
                  if (ok) setNewOption("");
                })
              }
            >
              Add
            </button>
          </div>
        </div>
      ) : null}

      <div className="field-editor-section">
        <div className="section-label">
          <span>Where it applies</span>
        </div>
        {field.scopes.length === 0 ? (
          <p className="muted" style={{ margin: "4px 0" }}>
            Every {field.level}.
          </p>
        ) : (
          field.scopes.map((s) => (
            <div key={s.id} className="field-editor-line">
              <span>{s.label}</span>
              <button
                className="linkish"
                disabled={busy}
                onClick={() => void run(() => api.removeFieldScope(fetcher, s.id))}
              >
                Remove
              </button>
            </div>
          ))
        )}
        <div className="row" style={{ gap: 8, marginTop: 6 }}>
          <select value={scopeValue} onChange={(e) => setScopeValue(e.target.value)} style={{ maxWidth: 320 }}>
            <option value="">Limit it to…</option>
            {scopeOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <button
            className="secondary"
            disabled={busy || scopeValue === ""}
            onClick={() => {
              const [k, v] = scopeValue.split(/:(.*)/s) as [string, string];
              void run(() => api.addFieldScope(fetcher, field.id, { [k]: v })).then((ok) => {
                if (ok) setScopeValue("");
              });
            }}
          >
            Add
          </button>
        </div>
      </div>

      {error ? <p className="error-inline">{error}</p> : null}
      <div className="row" style={{ justifyContent: "space-between", marginTop: 12 }}>
        <button
          className={field.isActive ? "destructive" : "secondary"}
          disabled={busy}
          onClick={() => void run(() => api.updateField(fetcher, field.id, { isActive: !field.isActive }))}
        >
          {field.isActive ? "Switch the field off" : "Switch the field on"}
        </button>
        <div className="row" style={{ gap: 8 }}>
          <button onClick={onClose} disabled={busy}>
            Close
          </button>
          <button
            className="primary"
            disabled={busy}
            onClick={() =>
              void run(() =>
                api.updateField(fetcher, field.id, {
                  label,
                  helpText: helpText.trim() === "" ? null : helpText,
                  required,
                  visibleTo,
                  editableBy,
                  inGrid,
                }),
              ).then((ok) => {
                if (ok) onSaved();
              })
            }
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
